// Caso de uso de la compañía (CC-CONSOLE #1614).
//
//   GET /company/use-case  → { companyId, useCase, canEdit, options }
//   PUT /company/use-case  ← { useCase }
//
// La compañía la resuelve el servidor (la de la sesión); sólo un admin de la
// compañía o la plataforma puede cambiarlo. Se manda también la compañía
// elegida en el selector del encabezado (`localStorage.companyId`): el
// servidor sólo la toma en cuenta para un administrador de plataforma, que no
// tiene compañía propia (sin ella respondía 404 y la tarjeta no cargaba).

import api from "../utils/api";
import { normalizeUseCase, parseUseCase, type UseCase } from "../utils/useCases";

const apiUrl = import.meta.env.VITE_FIREBASE_API_URL;

export interface CompanyUseCaseState {
  companyId: string;
  /** Normalizado por la API: sin dato guardado es `transportista`. */
  useCase: UseCase;
  canEdit: boolean;
  /**
   * ¿Está GUARDADO en la compañía (CC-CONSOLE #1634)? Falso en compañías viejas
   * y en APIs anteriores que no mandan la bandera.
   */
  stored: boolean;
}

/** `{ meta, data: { … } }` o el objeto plano: se aceptan las dos formas. */
const payloadOf = (body: unknown): Record<string, unknown> => {
  const root = (body as { data?: unknown })?.data ?? body;
  const inner = (root as { data?: unknown })?.data;
  return ((inner && typeof inner === "object" ? inner : root) || {}) as Record<string, unknown>;
};

/** Compañía elegida en el selector del encabezado, si hay. */
export const selectedCompanyId = (): string => {
  try {
    return (localStorage.getItem("companyId") || "").trim();
  } catch {
    return "";
  }
};

export async function fetchCompanyUseCase(): Promise<CompanyUseCaseState> {
  const id = selectedCompanyId();
  const res = await api.get(`${apiUrl}/company/use-case`, id ? { params: { id } } : undefined);
  const data = payloadOf(res.data);
  return {
    companyId: String(data.companyId || ""),
    useCase: normalizeUseCase(data.useCase),
    canEdit: data.canEdit === true,
    stored: data.useCaseStored === true,
  };
}

export interface SavedUseCase {
  useCase: UseCase;
  /**
   * ¿Quedó ajustado el menú del creador al nuevo caso de uso? `false` cuando
   * el API no pudo (perfil sin sembrar, accesos puestos a mano…); `null` si no
   * hubo cambio.
   */
  accessAssigned: boolean | null;
}

export async function saveCompanyUseCase(useCase: UseCase): Promise<SavedUseCase> {
  const companyId = selectedCompanyId();
  const res = await api.put(`${apiUrl}/company/use-case`, companyId ? { useCase, companyId } : { useCase });
  const data = payloadOf(res.data);
  const access = data.access as { assigned?: unknown } | null | undefined;
  return {
    useCase: parseUseCase(data.useCase) ?? useCase,
    accessAssigned: access && typeof access.assigned === "boolean" ? access.assigned : null,
  };
}
